/* eslint-disable no-unused-vars */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import { usePermissions } from "../../../../hooks/usePermissions";
import useLoader from "../../../../hooks/useLoader";
import { detailsMaintenanceMovementFields } from "../../../../constants/forms/movementFields";
import DetailsSectionRenderer from "../../../../components/DetailsSectionRenderer";
import useForm from "../../../../hooks/useForm";
import { setDefaultFieldValues } from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import ListHeader from "../../../../components/ListHeader";
import useAction from "../../../../hooks/useAction";
import { formatDateToInput } from "../../../../utils/formatDateToInput";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import { useQuery } from "@tanstack/react-query";
import useNotification from "../../../../hooks/useNotification";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import TableDetails from "../../../../components/table/TableDetails";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const DetailsMaintenanceMovementPage = () => {
  const navigate = useNavigate();
  const { movementId } = useParams();

  const { canAccess } = usePermissions();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, getByColumn: baseServiceGetByColumn } =
    useBaseService(navigate);

  // useForm para os detalhes do movimento principal
  const { formData, setFormData, formatData } = useForm(
    setDefaultFieldValues(detailsMaintenanceMovementFields),
  );

  // --- Padronização de filtros via useUrlFilters para os itens do movimento ---
  const { filters, pagination } = useUrlFilters(
    {
      deleted_at: false,
    },
    `movementItems_${movementId}`,
  );

  // Hook useAction para gerenciar ações de exclusão/ativação com modal de confirmação
  const {
    openModalConfirmation,
    action,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // --- Lógica de busca dos detalhes do movimento principal com TanStack Query ---
  const {
    data: movementDetailsResponseData,
    isLoading: isLoadingMovementDetails,
    isError: isErrorMovementDetails,
    error: movementDetailsQueryError,
  } = useQueryWithLoader({
    queryKey: ["movementDetails", movementId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.movements.getById(movementId),
      );
      return response.result;
    },
    enabled: !!movementId,
    staleTime: 60 * 1000, // 60 segundos de cache
  });

  // Efeito para exibir notificações de erro ao buscar detalhes do movimento
  useEffect(() => {
    if (
      !isLoadingMovementDetails &&
      isErrorMovementDetails &&
      movementDetailsQueryError
    ) {
      console.error(
        "DetailsMaintenanceMovementPage: Erro ao carregar detalhes do movimento (useQuery):",
        movementDetailsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar detalhes do movimento: ${
          movementDetailsQueryError.response?.data?.message ||
          movementDetailsQueryError.message
        }`,
      );
    }
  }, [
    isLoadingMovementDetails,
    isErrorMovementDetails,
    movementDetailsQueryError,
    showNotification,
  ]);

  // Efeito para formatar e definir os dados do formulário quando a resposta da query estiver disponível
  useEffect(() => {
    if (
      !isLoadingMovementDetails &&
      !isErrorMovementDetails &&
      !movementDetailsQueryError &&
      movementDetailsResponseData
    ) {
      hideLoader();
      // Formata os dados principais do movimento para o formulário
      formatData(movementDetailsResponseData, detailsMaintenanceMovementFields);

      // Ajusta o formData com nomes amigáveis para exibição ou dados adicionais necessários
      setFormData((prev) => ({
        ...prev,
        recipient_customer_id:
          movementDetailsResponseData.recipient_customer_name ||
          prev.recipient_customer_id,
        organization_id:
          movementDetailsResponseData.organization_name || prev.organization_id,
        movement_date: formatDateToInput(
          movementDetailsResponseData.movement_date,
        ),
        status_id: movementDetailsResponseData.status_name || prev.status_id,
        service_order_id:
          movementDetailsResponseData.service_order_id || prev.service_order_id,
      }));
    }
  }, [
    isLoadingMovementDetails,
    isErrorMovementDetails,
    movementDetailsQueryError,
    movementDetailsResponseData,
    hideLoader,
    formatData,
    setFormData,
  ]);

  // --- Lógica de busca dos ITENS do movimento com TanStack Query ---
  const {
    data: movementItemsResponseData,
    isLoading: isLoadingMovementItems,
    isError: isErrorMovementItems,
    error: movementItemsQueryError,
  } = useQueryWithLoader({
    queryKey: ["movementItems", movementId, filters, pagination],
    queryFn: async () => {
      showLoader();
      const filtersTranformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(
        entities.movements.items.get(movementId),
        params,
      );
      return response.result;
    },
    staleTime: 60 * 1000, // 60 segundos de cache
    keepPreviousData: true, // Mantém dados anteriores enquanto busca novos
    select: (data) => {
      return {
        movementsEquipments: data.data.map((item) => ({
          id: item.id,
          equipment: item.equipment_name ?? "-",
          movement_type: item.movement_name ?? "-",
          status: item.status_name ?? "-",
          isDeleted: !!item.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { movementsEquipments = [], totalPages = 1 } =
    movementItemsResponseData || {};

  const columns = [
    { key: "equipment", label: "Patrimônio" },
    { key: "movement_type", label: "Tipo de Movimento" },
    { key: "status", label: "Status" },
  ];

  // Efeito para exibir notificações de erro ao buscar itens do movimento
  useEffect(() => {
    if (!isLoadingMovementItems) {
      hideLoader();
    }
    if (
      !isLoadingMovementItems &&
      isErrorMovementItems &&
      movementItemsQueryError
    ) {
      console.error(
        "DetailsMaintenanceMovementPage: Erro ao carregar itens do movimento (useQuery):",
        movementItemsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar itens do movimento: ${
          movementItemsQueryError.response?.data?.message ||
          movementItemsQueryError.message
        }`,
      );
    }
  }, [
    isLoadingMovementItems,
    isErrorMovementItems,
    movementItemsQueryError,
    showNotification,
    hideLoader,
  ]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Movimento - Manutenção"
        showBackButton={true}
        backUrl="/aluguel/movimentos"
      />

      <div className="container-fluid p-1">
        {/* Renderiza os detalhes do movimento principal */}
        <DetailsSectionRenderer
          sections={detailsMaintenanceMovementFields}
          formData={formData}
          readOnly
        />

        <div className="my-2">
          <ListHeader
            title="Item do Movimento"
            buttonIsNotVisible={true}
            canAccess={canAccess}
          />
        </div>
        {/* Tabela dinâmica para exibir os equipamentos do movimento */}
        <TableDetails data={movementsEquipments} columns={columns} />
      </div>

      {/* Modal de confirmação para ações de exclusão/ativação */}
      <ConfirmationModal
        open={openModalConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmAction}
        itemName={selectedItem ? `${selectedItem.equipment}` : ""}
        text={action.text}
      />
    </MainLayout>
  );
};

export default DetailsMaintenanceMovementPage;
